import {
  Alert,
  AlertDescription,
  AlertTitle,
  Button,
  CloseButton,
  HStack,
  VStack,
} from '@chakra-ui/react';

interface ActionToastProps {
  title: string;
  description: string;
  status: 'success' | 'warning' | 'info';
  actionLabel: string;
  onAction: () => void;
  onClose: () => void;
}

/**
 * The action button's variant, per toast status. Exhaustive by construction:
 * `Record<ActionToastProps['status'], …>` means adding a fourth status is a
 * compile error here rather than a silent fall-through to whichever branch a
 * ternary happened to end on.
 */
const ACTION_VARIANT: Record<ActionToastProps['status'], string> = {
  success: 'outline-success',
  warning: 'outline-warning',
  info: 'outline',
};

/**
 * Custom toast component with an action button (e.g. "View", "Open").
 * Reusable toast component with an action button (e.g. "View", "Undo").
 */
export function ActionToast({
  title,
  description,
  status,
  actionLabel,
  onAction,
  onClose,
}: ActionToastProps) {
  return (
    <Alert
      status={status}
      variant="left-accent"
      borderRadius="md"
      boxShadow="lg"
      pr={2}
      alignItems="start"
    >
      <VStack align="start" spacing={1} flex={1} mr={2}>
        <AlertTitle fontSize="sm" fontWeight="bold">
          {title}
        </AlertTitle>
        <AlertDescription fontSize="sm" display="block">
          {description}
        </AlertDescription>
      </VStack>
      <HStack spacing={2} alignSelf="center">
        <Button
          size="sm"
          // Was a `colorScheme` ternary, inert on Button, so the action read
          // identically whether the toast was reporting success or a problem.
          //
          // TOTAL over `status`, which is THREE-valued. A two-branch ternary
          // here collapsed `info` onto the cautioning variant, and `info` is
          // what seven of the nine call sites pass — including "Moved to
          // trash", the most-seen toast in the app. That shipped a blue banner
          // with an amber Undo button, which is worse than the neutral one it
          // replaced. `info` keeps the plain `outline` it has always had.
          variant={ACTION_VARIANT[status]}
          onClick={() => {
            onAction();
            onClose();
          }}
        >
          {actionLabel}
        </Button>
        <CloseButton size="sm" onClick={onClose} />
      </HStack>
    </Alert>
  );
}
